import url from "../constants/urlList";
import { CONSTANTS } from "../constants/app-constant";
const errMsg = 'Something went wrong';

const doGetCall = (apiUrl) => {
    return fetch(apiUrl)
        .then(async (response) => {
            if (response.status === 200) {
                return response.json();
            }

            return {
                status: response.status,
                // A non-200 may carry no JSON body (e.g. an empty-body 404 from a
                // flag-gated endpoint, or a proxy HTML page). Keep the status either
                // way so callers can classify it; only a network reject loses it.
                errMsg: await response?.json().catch(() => errMsg)
            };
        })
        .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
};

const doUpdateCall = (apiUrl, data, isPutCall = false) => {
    const requestOptions = {
        method: isPutCall ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" }
    };

    if (data !== undefined) {
        requestOptions.body = JSON.stringify(data);
    }

    return fetch(apiUrl, requestOptions)
        .then(async (response) => {
            if (response.status === 200 || response.status === 201) {
                return response.json();
            }

            return {
                errStatus: response.status,
                // A non-2xx may carry no JSON body (flag-gated empty 404, proxy HTML).
                // Guard the parse so it can't reject into the outer catch and get
                // mislabeled a network "500" — that would hide a dark 404 (feature
                // off) as a server error. Mirrors doGetCall's contract.
                errMsg: await response?.json().catch(() => errMsg)
            };
        })
        .catch(() => {
            return {
                errStatus: "500",
                errMsg: CONSTANTS.errorMsgs[500],
            };
        });
};

const doDeleteCall = (apiUrl) => {
    const requestOptions = {
        method: "DELETE",
        headers: { "Content-Type": "application/json" }
    };

    return fetch(apiUrl, requestOptions)
        .then(async (response) => {
            if (response.status === 200) {
                return response.json();
            }

            return {
                errStatus: response.status,
                // A non-2xx may carry no JSON body (flag-gated empty 404, proxy HTML).
                // Guard the parse so it can't reject into the outer catch and get
                // mislabeled a network "500" — that would hide a dark 404 (feature
                // off) as a server error. Mirrors doGetCall's contract.
                errMsg: await response?.json().catch(() => errMsg)
            };
        })
        .catch(() => {
            return {
                errStatus: "500",
                errMsg: CONSTANTS.errorMsgs[500],
            };
        });
};

const doPostNoContentCall = (apiUrl) => {
    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" }
    };

    return fetch(apiUrl, requestOptions)
        .then((response) => ({
            ok: response.ok,
            status: response.status
        }))
        .catch(() => ({
            ok: false,
            status: 500
        }));
};

export const api = {
    addUserApi(user) {
        return doUpdateCall(url.addUser, user);
    },
    doLogin(user) {
        return doUpdateCall(url.login, user);
    },
    // Native Sign in with Apple (Step-1 native-auth infra). Backend verifies the
    // identityToken against Apple's JWKS and completes the shared session path.
    appleSignIn(appleAuthData) {
        return doUpdateCall(url.appleAuth, appleAuthData);
    },
    getUserDetails(userId) {
        return doGetCall(url.userDetails + userId);
    },
    getUserDetailsFromToken() {
        return doGetCall(url.userDetailsFromToken);
    },
    createQuiz(quizData) {
        return doUpdateCall(url.createQuiz, quizData);
    },
    startAbsiteDiagnostic(diagnosticData) {
        return doUpdateCall(url.startAbsiteDiagnostic, diagnosticData);
    },
    requestAbsiteProgramCode(requestData) {
        return doUpdateCall(url.absiteProgramCodeRequest, requestData);
    },
    listAbsiteProgramCodes(filters = {}) {
        const queryParams = new URLSearchParams();
        if (filters.status) {
            queryParams.append('status', filters.status);
        }

        const query = queryParams.toString();
        return doGetCall(`${url.absiteProgramCodes}${query ? `?${query}` : ''}`);
    },
    createAbsiteProgramCode(programCodeData) {
        return doUpdateCall(url.absiteProgramCodes, programCodeData);
    },
    updateAbsiteProgramCode(programCode, programCodeData) {
        return doUpdateCall(
            `${url.absiteProgramCodes}/${encodeURIComponent(programCode)}`,
            programCodeData,
            true
        );
    },
    getAbsiteDiagnosticReport(quizId) {
        return doGetCall(`${url.diagnostic}/${quizId}/report`);
    },
    getAbsiteProgramSummary(filters = {}) {
        const queryParams = new URLSearchParams();
        if (filters.programCode) {
            queryParams.append('programCode', filters.programCode);
        }
        if (filters.from) {
            queryParams.append('from', filters.from);
        }
        if (filters.to) {
            queryParams.append('to', filters.to);
        }

        const query = queryParams.toString();
        return doGetCall(`${url.absiteProgramSummary}${query ? `?${query}` : ''}`);
    },
    // --- Generic ReviewQueue (new /qlearn/review/* backend; flag-gated dark) ---
    // Reviewer worklist: PENDING items of one type. Grouped client-side by
    // subjectKey (=questionId) into per-question candidate sets.
    getReviewQueue(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.unreviewedByMe !== undefined) queryParams.append('unreviewedByMe', filters.unreviewedByMe);
        // status=PENDING (default, omitted) | DECIDED | ALL. The status rail's
        // DECIDED fetch; an old backend ignores the param and returns PENDING —
        // callers guard via filterDecidedItems (mixed-deploy safe both ways).
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/queue?${queryParams.toString()}`);
    },
    // Admin queue-status snapshot: two-axis counts + reviewer activity for a type.
    getReviewQueueStatus(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        return doGetCall(`${url.reviewQueue}/queue/status?${queryParams.toString()}`);
    },
    // Reviewer FLAG upsert. Body: { decision, isBest, comment } (image type).
    // isBest is exclusive per question server-side (picking one clears siblings).
    voteReviewItem(itemId, body) {
        return doUpdateCall(`${url.reviewQueue}/item/${itemId}/vote`, body);
    },
    // #584 triple-review (rq-triple-review-contract §5), reconciled to QLearn-service#579.
    // All-candidates read for one question → QuestionCandidatesDto: the FULL candidate
    // set (decided included, AD-5) as ReviewItemDto[] + each item's reviewStatus + the
    // caller's saved myVote + assignment { assignedToMe, canRate, assignedReviewerIds
    // (admin-only) }, provenance/model redacted for non-admins (AD-4). The queue LIST is
    // QuestionCandidatesPage { questions: QuestionCandidatesDto[], totalElements, ... }.
    // 404 while the flag is dark. Shapes handled by review-queue-logic.adaptQuestionResponse
    // / flattenQuestionsResponse.
    getReviewQuestion(subjectKey) {
        return doGetCall(`${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}`);
    },
    // All-candidates queue LIST (AD-5): GET /qlearn/review/questions?type&examType&page&size
    // → QuestionCandidatesPage { questions: QuestionCandidatesDto[] (each with the FULL
    // active candidate set — decided included — + group assignment), totalElements, ... }.
    // This is the #584 P1 grid source: unlike /queue (PENDING-only worklist), it returns
    // every question's complete candidate set so a mixed (pending+decided) question renders
    // ALL its candidates in one N-up grid. 404 while the flag is dark. Shapes normalized by
    // review-queue-logic.flattenQuestionsResponse.
    getReviewQuestionsPage(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/questions?${queryParams.toString()}`);
    },
    // ONE comparative decision per question group (#584 A2, T-CMP U1): PICK_BEST or
    // REJECT_SET, derived server-side into the unchanged per-candidate rows. Assigned
    // tutor or admin (server-enforced); 409 when the group has no PENDING candidate.
    decideReviewQuestion(subjectKey, body, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        return doUpdateCall(
            `${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}/decision?${queryParams.toString()}`,
            body
        );
    },
    // Admin-only reviewer assignment (AD-2), keyed subjectKey + reviewType. The ACL is
    // server-enforced (@PreAuthorize ROLE_ADMIN on every endpoint); a non-admin caller
    // gets a non-2xx which the doGetCall/doUpdateCall/doDeleteCall contract surfaces as
    // { status|errStatus }, so the admin-only UI simply stays hidden.
    // List active assignees for a question → ReviewAssignmentDto[] (assignedReviewerId etc.).
    getReviewAssignments(subjectKey, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('subjectKey', subjectKey);
        queryParams.append('type', type);
        return doGetCall(`${url.reviewQueue}/assignments?${queryParams.toString()}`);
    },
    // Assign a reviewer. Body: ReviewAssignmentRequest { subjectKey, reviewType, reviewerId }.
    // Idempotent server-side (re-activates an existing row); returns ReviewAssignmentDto.
    assignReviewer(subjectKey, reviewerId, reviewType = 'IMAGE_CANDIDATE') {
        return doUpdateCall(`${url.reviewQueue}/assignments`, { subjectKey, reviewType, reviewerId });
    },
    // Revoke a reviewer (soft-delete; idempotent no-op if none active). The endpoint takes
    // subjectKey/type/reviewerId as QUERY params (DELETE has no body). Returns { revoked: true }.
    revokeReviewer(subjectKey, reviewerId, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('subjectKey', subjectKey);
        queryParams.append('type', type);
        queryParams.append('reviewerId', reviewerId);
        return doDeleteCall(`${url.reviewQueue}/assignments?${queryParams.toString()}`);
    },
    // Admin-only reset (AD-3): reopens all items in the question to PENDING, supersedes
    // (never deletes) prior votes, writes a ReviewResetAudit. Body { confirm, reason }.
    // AD-6: 409 when a candidate is PUBLISHED/RETRACTED (reset invalid) — surfaced
    // non-destructively caller-side. doUpdateCall resolves { errStatus } on non-2xx.
    resetReviewQuestion(subjectKey, body) {
        return doUpdateCall(`${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}/reset`, body);
    },
    // Phase-2 reasoning-clarify round on this reviewer's vote. `answer` undefined
    // triggers the sufficiency check + first follow-up; a string answers the open
    // follow-up. 404 while the clarify flag is dark (handled gracefully caller-side).
    clarifyReviewItem(itemId, answer) {
        return doUpdateCall(`${url.reviewQueue}/item/${itemId}/clarify`,
            answer === undefined ? {} : { answer });
    },
    // Phase-2 per-question suitability toggle. Body:
    // { questionId, examType, unsuitableForImage?, unsuitableForTable?, imageReason?, tableReason? }.
    // A null/omitted flag leaves that axis unchanged server-side. 404 while the
    // suitability flag is dark (handled gracefully caller-side).
    markSuitability(body) {
        return doUpdateCall(`${url.reviewQueue}/suitability`, body);
    },
    // Suitability read for the dashboard tile (+ producers). 404 while dark.
    getSuitability(examType) {
        const query = examType ? `?examType=${encodeURIComponent(examType)}` : '';
        return doGetCall(`${url.reviewQueue}/suitability${query}`);
    },
    // Phase-3.3 read-only per-question drill-down: every ReviewQueue signal for one
    // question (items + reasoning + clarify threads + suitability + corrections). 404 dark.
    getQuestionDrilldown(questionId) {
        return doGetCall(`${url.reviewQueue}/question/${encodeURIComponent(questionId)}/drilldown`);
    },
    // Read-only operator analytics (throughput/latency/backlog/outcomes/clarify). 404 dark.
    getReviewAnalytics({ type = "IMAGE_CANDIDATE", examType = "ABSITE", windowDays = 0 } = {}) {
        const q = new URLSearchParams({ type, examType, windowDays });
        return doGetCall(`${url.reviewQueue}/analytics?${q.toString()}`);
    },
    // --- Admin publish console (all three @Secured(ROLE_ADMIN) server-side; 404 dark) ---
    // Publish worklist: APPROVED items not yet published.
    getPublishPending(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/publish/pending?${queryParams.toString()}`);
    },
    // Batch publish (the explicit two-stage promotion step — side-car CDN copy,
    // never a bank write). Returns { publishBatchId, published, skipped[{id,reason}] }.
    // S1.2/F-S5-1: optional extras carry the PER-ITEM stemLeakageAttestedItemIds and
    // overrideItemIds (+ required overrideReason) — never batch-level booleans
    // (the server 400s the retired overrideDangerFlagged boolean by design).
    publishReviewItems(itemIds, extras) {
        return doUpdateCall(`${url.reviewQueue}/publish`, { itemIds, ...(extras || {}) });
    },
    // Emergency rollback of one publish batch. Returns { retracted: [ids] }.
    retractPublishBatch(batchId) {
        return doUpdateCall(`${url.reviewQueue}/publish/${encodeURIComponent(batchId)}/retract`, undefined);
    },
    // Correction console: the read-only QBank apply export (staged mutations + diffs). 404 dark.
    getBankMutationExport(examType = "ABSITE") {
        return doGetCall(`${url.reviewQueue}/bank-mutations/export?examType=${encodeURIComponent(examType)}`);
    },
    // Content-quality analytics (read-only). 404 while dark. Flagged worst-first list +
    // per-question composed health (difficulty + discrimination + anomaly flags).
    getContentQualityFlagged(limit = 50) {
        return doGetCall(`${url.contentQuality}/questions/flagged?limit=${encodeURIComponent(limit)}`);
    },
    getContentQualityHealth(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/health`);
    },
    getContentQualityDistractors(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/distractors`);
    },
    getContentQualityCategories(dimension = "organSystem", limit = 50) {
        return doGetCall(`${url.contentQuality}/categories?dimension=${encodeURIComponent(dimension)}&limit=${encodeURIComponent(limit)}`);
    },
    getContentQualityTrend(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/trend`);
    },
    // SME CSV download (read-only). The global fetch is auth-patched, so a plain <a href>
    // could not carry the Bearer token — fetch the bytes here and return a Blob (or a
    // { status } marker on non-200 so the caller can render the dark/error state).
    getContentQualityDistractorCsv(limit = 50) {
        return fetch(`${url.contentQuality}/distractors/export?limit=${encodeURIComponent(limit)}`)
            .then((res) => (res.ok ? res.blob() : { status: res.status }))
            .catch((err) => err);
    },
    // NOTE: no applyBankMutation client method by design — the APPLY boundary is
    // owner-gated (Q5 ROLE_PUBLISHER, Akshay-gated) and the backend endpoint always
    // 403s. The console UI's Apply control is permanently disabled; do NOT add a
    // client caller here.
    // Role-gated proxy for an un-published candidate image → object URL (the
    // patched global fetch attaches the Bearer token; a plain <img src> could not).
    getReviewItemImage(itemId) {
        return fetch(`${url.reviewQueue}/item/${itemId}/image`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`image request failed: ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => URL.createObjectURL(blob));
    },
    // S3-U1 / R-P1-1: role-gated proxy for a grounding reference asset image → object URL.
    // Dedicated endpoint (readImage is hard-bound to IMAGE_CANDIDATE items); same Bearer-gated
    // fetch-then-object-URL pattern as getReviewItemImage. @PreAuthorize TUTOR||ADMIN, dark-404.
    getReferenceAssetImage(assetId) {
        return fetch(`${url.reviewQueue}/reference-assets/${encodeURIComponent(assetId)}/image`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`reference image request failed: ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => URL.createObjectURL(blob));
    },
    claimNotesToQuiz(token) {
        return doUpdateCall(`${url.notesToQuiz}/${encodeURIComponent(token)}/claim`, undefined);
    },
    // Persist the session-fiducial id-book (full cumulative
    // {eventId, recordingId, qToTag} from SessionIdBook.toJSON()) so the
    // Zoom-recording decoder can map painted tag indices back to question-ids.
    // PUT = idempotent upsert keyed by eventId.
    upsertSessionFiducialIdBook(idBookPayload) {
        return doUpdateCall(url.sessionFiducialIdBook, idBookPayload, true);
    },
    trackNotesToQuizOpen(token) {
        return doPostNoContentCall(`${url.notesToQuiz}/${encodeURIComponent(token)}/opened`);
    },
    saveAbsiteDiagnosticProfile(profileData) {
        return doUpdateCall(
            `${url.diagnostic}/${profileData.quizId}/profile`,
            profileData.profile,
            true
        );
    },
    submitQuiz(quizData) {
        const quizUrl = `${url.submitQuiz}/${quizData.quizHistory.quizId}/user/${quizData.quizHistory.userId}/submit`;

        if (quizData?.quizEndMode === "ADHOC") {
            const adhocPayload = { quizEndMode: quizData.quizEndMode, quizHistory: { quizSubmitDate: quizData?.quizHistory?.quizSubmitDate } };
            return doUpdateCall(quizUrl, adhocPayload);
        } 
        return doUpdateCall(quizUrl, quizData);
    },
    // Server-authoritative per-question grading (in-quiz egress Slice B;
    // QLearn-service #483). The caller's Bearer token rides the patched global
    // fetch, and the server enforces owner + tutorOn + IN_PROGRESS before
    // grading. Returns { correct, question } where question is the REVEALED
    // view (answer key + explanations) — the answer is final server-side, so a
    // re-POST returns the first recorded result and never re-grades.
    // Runtime client config (in-quiz egress C0; QLearn-service #489). The
    // endpoint is authenticated: an unauthenticated fetch returns a non-200
    // shape and the caller no-ops — fail-open to flag-OFF (today's path).
    getClientConfig() {
        return doGetCall(url.clientConfig);
    },
    submitQuestionAnswer(quizId, questionId, selectedAnswer) {
        return doUpdateCall(
            `${url.submitQuiz}/${quizId}/question/${encodeURIComponent(questionId)}/answer`,
            { selectedAnswer }
        );
    },
    // Guided Learning (GL-1 §1/§3) client callers, per the server-owned wire
    // contract (docs/gl-api-wire-contract.md, QLearn-service). /gl/why and
    // /gl/assisted callers were REMOVED (ROW1 gate B4/B5 cure): WHY rides the
    // card-draft call at grade time, and the assisted signal of record is the
    // server-side hint-serve row. These return the standard {errStatus, errMsg}
    // non-200 shape; gl-service.js adapts it to the typed glError contract.
    requestGlHint({ questionId, hintIndex }) {
        // NEVER carries WHY (ruling C-1/C-9).
        return doUpdateCall(url.glHint, { questionId, hintIndex });
    },
    postGlEvent(record) {
        // `record` is already allowlist-sanitized by gl-events.buildGlEvent;
        // the server re-redacts and overrides the subject key regardless.
        return doUpdateCall(url.glEvent, record);
    },
    getCategories() {
        return doGetCall(url.categories);
    },
    getQuesCategories() {
        return doGetCall(url.questionCategories);
    },
    getQuestionHistory(quizId) {
        return doGetCall(url.questionHistory + quizId);
    },
    getQuiz(quizId) {
        return doGetCall(url.getQuiz + quizId);
    },
    getQuizHistory(quizId) {
        return doGetCall(url.quizHistory + quizId);
    },
    getStudentQuizHistory(quizId) {
        return doGetCall(url.studentQuizHistory + quizId);
    },
    getOverallQuizHistory(quizId) {
        return doGetCall(url.overallQuizHistory + quizId);
    },
    getOverallStudentQuiz(params) {
        const status = params.isCompleted ? "COMPLETE" : "IN_PROGRESS";
        return doGetCall(`${url.overallStudentQuiz}/${params.userId}?status=${status}`);
    },
    getQuestionMetadata(questionList) {
        // In-quiz egress C1b: an optional quizId names the quiz this fetch
        // belongs to and rides as a QUERY param (QLearn-service #491), so the
        // server can reveal answered-final questions once the strip flag is
        // on. It never rides the body — QuestionByIdRequest has no such field
        // — and absent quizId keeps the request byte-identical to today.
        const { quizId, ...body } = questionList || {};
        const requestUrl = quizId
            ? `${url.questionMetadata}?quizId=${encodeURIComponent(quizId)}`
            : url.questionMetadata;
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        };
        return fetch(requestUrl, requestOptions)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    // Admin-only author attribution for the review-drawer byline (the fields
    // were removed from the student-facing question payload in QLearn-service
    // #484; this endpoint is @PreAuthorize(ROLE_ADMIN)). Returns
    // {questionId,userId,userEmail}; a non-admin/anon caller gets a non-200
    // which doGetCall surfaces as {status,...} so the byline just stays hidden.
    getQuestionAuthor(questionId) {
        return doGetCall(`${url.questionMetadata}/${questionId}/author`);
    },
    reviewQuestion(param) {
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(param.reviewTime),
        };

        return fetch(
            `${url.reviewQuestion}/${param.questionId}/review`,
            requestOptions
        )
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    // getPackageList() {
    //     return doGetCall(url.packagePlan);
    // },
    getUserPackage(userid) {
        return doGetCall(`${url.userDetails}${userid}/plan/true`);
    },
    createPaymentSession(paymentData) {
        return doUpdateCall(url.paymentSession, paymentData);
    },
    updatePaymentStatus(param) {
        const statusUrl = param.isSuccess ? `${url.booking}/${param.bookingId}/status/SUCCESS` :
            `${url.booking}/${param.bookingId}/status/FAILED`;

        return doUpdateCall(statusUrl, undefined, true);
    },
    setUserActivity(paramData) {
        return doUpdateCall(url.userActivity + paramData.userId, paramData, true);
    },
    getUserActivity(userId) {
        return doGetCall(url.userActivity + userId);
    },
    getUserPayments(userId) {
        return doGetCall(url.userPayments + userId);
    },
    getInvitations() {
        return doGetCall(url.invitation + '?page=0&size=100');
    },
    cancelSubscription(bookingId) {
        return doUpdateCall(`${url.booking}/${bookingId}/${url.cancelSubscription}`, undefined);
    },
    sendInvitation(paramBody) {
        return doUpdateCall(url.paymentSession, paramBody);
    },
    resendInvitation(invitationId) {
        return doUpdateCall(`${url.invitation}/${invitationId}`, undefined, true);
    },
    getPreference(userId) {
        return doGetCall(`${url.preference}/${userId}`);
    },
    setPreference(param) {
        return doUpdateCall(`${url.preference}/${param.userId}`, param.body, true);
    },
    setFeedback(paramBody) {
        return doUpdateCall(url.paymentSession, paramBody);
    },
    setComment(param) {
        return doUpdateCall(`${url.comment}/${param.quesId}/comment`, param.commentParam);
    },
    getComment(param) {
        return doGetCall(`${url.comment}/${param.quesId}/comment`);
    },
    getImage(imageName) {
        return doGetCall(`${url.image}?imageName=${imageName}`);
    },
    getQuesFeedback(quesId) {
        return doGetCall(`${url.quesFeedback}/${quesId}`);
    },
    setQuesFeedback(paramBody) {
        return doUpdateCall(url.quesFeedback, paramBody);
    },
    getQuestionCount(paramBody) {
        return doUpdateCall(url.quesCatCount, paramBody);
    },
    getQuesTutorFeedback(quesId) {
        return doGetCall(`${url.quesTutorFeedback}/${quesId}`);
    },
    setQuesTutorFeedback(paramBody) {
        return doUpdateCall(url.quesTutorFeedback, paramBody);
    },
    getTutorFeedbackAll() {
        return doGetCall(url.tutorFeedbackAll);
    },
    getStudentFeedbackAll() {
        return doGetCall(url.studentFeedbackAll);
    },
    createTutorFeedbackBlock(block) {
        return doUpdateCall(url.feedbackBlock, block);
    },
    endTutorFeedbackBlock(blockId) {
        return doUpdateCall(`${url.feedbackBlock}/${blockId}/end`, undefined, true);
    },
    getAllPlans() {
        return doGetCall(url.packagePlan);
    },
    updatePlan(planData) {
        return doUpdateCall(`${url.packagePlan}/${planData.id}`, planData, true);
    },
    createPlan(planData) {
        return doUpdateCall(url.packagePlan, planData);
    },
    deletePlan(planId) {
        return doDeleteCall(`${url.packagePlan}/${planId}`);
    },
    selectPlan(body) {
        return doUpdateCall(`${url.userDetails}${body.userId}/plan/${body.planId}/select`, undefined, true);
    },
    getActivePlans(userId) {
        return doGetCall(`${url.userDetails}${userId}/plan?status=ACTIVE,CANCELLED,TRIALING`);
    },
    getTrialEligibility(userId) {
        return doGetCall(`${url.userDetails}${userId}/trial-eligible`);
    },
    sendInvites(invitesParam) {
        return doUpdateCall(url.sendInvites, invitesParam);
    },
    // Admin-gated user roster by role (server @PreAuthorize ROLE_ADMIN). Defaults to
    // STUDENT so the existing refund saga (calls with no arg) is unchanged; the review
    // assignment UI passes "TUTOR" to fetch the assignable tutor roster (displayName,
    // email, id). A non-admin caller gets a non-2xx surfaced as { status } by doGetCall.
    getUsersByRole(role = 'STUDENT') {
        return doGetCall(`${url.getUsersByRole}?role=${encodeURIComponent(role)}`);
    },
    getPaymentsByUser(userId) {
        return doGetCall(`${url.userPayments}${userId}?status=SUCCESS,CANCELED`);
    },
    refundBooking(bookingData) {
        const dataPayload = JSON.parse(JSON.stringify(bookingData));
        delete dataPayload.bookingId;
        return doUpdateCall(`${url.booking}/${bookingData.bookingId}/refund`, dataPayload);
    },
    getCategoryPreferences(userId) {
        return doGetCall(`${url.categoryPreference}${userId}`);
    },
    saveCategoryPreferences(data) {
        return doUpdateCall(`${url.categoryPreference}${data.userId}`, data.categoryMap);
    },
    searchTopics(query) {
        return doGetCall(`${url.topicsSearch}${query}`);
    },
    publishQlinkUrl(data) {
        return doUpdateCall(`${url.publishQlink}`, data.qlinkData);
    },
    revokeQlinkUrl(data) {
        return doUpdateCall(`${url.revokeQlink}${data?.qLinkID}`, undefined, true);
    },
    recordQLinkQuestionAnswered(data) {
        return doUpdateCall(url.qlinkQuestionAnswered, data);
    },
    fetchQlinkUrl(data) {
        return doGetCall(`${url.fetchQlink}${data?.query ? `?${data?.query}` : ''}`);
    },
    getDemoQuestionMetadata(questionList) {
        const token = window.localStorage.getItem('userAcquisitiontoken');

        const apiUrl = `${url.questionMetadata}?token=${token}`;
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(questionList),
        };
        return fetch(apiUrl, requestOptions)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    getDemoQuestionIDs(qlinkId) {
        const token = window.localStorage.getItem('userAcquisitiontoken');
        const apiUrl = `${url.qlinkQues}/${qlinkId}?token=${token}`;

        return fetch(apiUrl)
            .then(async (response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    errStatus: response.status,
                    // Guard the parse so a bodyless non-2xx keeps its real status
                    // instead of rejecting into the outer catch as "500". Mirrors
                    // doGetCall / doUpdateCall.
                    errMsg: await response?.json().catch(() => errMsg)
                };
            })
            .catch(() => {
                return {
                    errStatus: "500",
                    errMsg: CONSTANTS.errorMsgs[500],
                };
            });
    },
    sendOTP(phoneData) {
        return doUpdateCall(url.sendOTP, phoneData);
    },
    verifyOTP(otpData) {
        return doUpdateCall(url.verifyOTP, otpData);
    },
    completeProfile(profileData) {
        return doUpdateCall(url.completeProfile, profileData);
    },
    getReferralCode() {
        return doGetCall(url.referralCode);
    },
    getReferralStats() {
        return doGetCall(url.referralStats);
    },
    // Semantic Power Search (flag-gated backend; empty-body 404 while disabled).
    // Uses a dedicated fetch (still authed via the global window.fetch override
    // in store.js) so a non-200 preserves the numeric status without calling
    // response.json() on an empty 404 body — which shared doUpdateCall does and
    // would turn the 404 into a generic rejection.
    powerSearch(payload) {
        return fetch(url.powerSearch, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        })
            .then((response) => {
                if (response.status === 200 || response.status === 201) {
                    return response.json();
                }
                return { errStatus: response.status };
            })
            // Truthy so the caller's error gate fires (a falsy 0 would read as "no error").
            .catch(() => ({ errStatus: -1 }));
    },
    // Hydrate question stems for the returned ids. Backend DTO binds the body as
    // `question_list` (@JsonProperty), matching the existing exam flow.
    getQuestionsByIds(questionIds) {
        return doUpdateCall(url.questionMetadata, { question_list: questionIds });
    },
    // Flag-gated "More Like This": top-N active questions nearest a seed question by stored
    // BRIEF_EXPLANATION vector (QLearn-service PR #428). 404 when the backend flag is off.
    getMoreLikeThis(seedQuestionId) {
        return doGetCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(seedQuestionId)}/more-like-this`);
    },
    // Tutor/admin signal on one served seed -> candidate recommendation. The server
    // upserts this reviewer pair; reviewer identity is always taken from auth.
    postMoreLikeThisFeedback(seedQuestionId, body) {
        return doUpdateCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(seedQuestionId)}/more-like-this/feedback`, body);
    },
    // Flag-gated Audio Mode manifest (QLearn-service audio tranche 1). 404 when the
    // backend flag is off, the caller is outside the server audience gate, or the
    // question has no audio — all uniform by design.
    getAudioManifest(questionId) {
        return doGetCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(questionId)}/audio-manifest`);
    },
    // Audio segment bytes → Blob (the getReviewItemImage precedent: the gated proxy
    // endpoint needs the Bearer token the patched global fetch attaches — a plain
    // <audio src> could not carry it). Manifest URLs are service-relative when
    // proxy-served and absolute when CDN-hosted; resolve both here.
    getAudioSegmentBlob(manifestUrl) {
        const absolute = /^https?:\/\//i.test(manifestUrl)
            ? manifestUrl
            : `${url.domain}/qlearn/${manifestUrl}`;
        return fetch(absolute)
            .then((res) => (res.ok ? res.blob() : { status: res.status }))
            .catch((err) => err);
    },
    // Tutor<->student mentorships (flag-gated; endpoints per
    // docs/qlearn-tutor-student-sharing-design.md). direction=incoming|outgoing,
    // status=PENDING|ACTIVE|... — both optional.
    listMentorships(params = {}) {
        const q = new URLSearchParams();
        if (params.direction) q.append("direction", params.direction);
        if (params.status) q.append("status", params.status);
        const qs = q.toString();
        return doGetCall(`${url.mentorships}${qs ? `?${qs}` : ""}`);
    },
    requestMentorship(body) {
        return doUpdateCall(url.mentorships, body);
    },
    // op = accept | decline | revoke (PUT /mentorships/{id}/{op}).
    respondToMentorship(mentorshipId, op) {
        return doUpdateCall(`${url.mentorships}/${mentorshipId}/${op}`, {}, true);
    },
    // Phase 2: a tutor reads a linked student's quiz history (server-side guarded
    // by an ACTIVE mentorship). Returns { studentName?, quizHistory:[...] } or a
    // quiz-history array; 404 until the backend guard/endpoint lands.
    getMentorshipStudentQuizHistory(studentUserId) {
        return doGetCall(`${url.mentorships}/students/${studentUserId}/quiz-history`);
    },
    // Phase 3: a tutor creates a quiz FOR a linked student (POST). Body:
    // { noOfQuestions, categoryMap, medicalCategoriesMap? }. The owner is the path
    // studentUserId; the server guards it with an ACTIVE mentorship granting
    // CREATE_QUIZ. 404s until the backend mentorship flag lands.
    assignMentorshipQuiz(studentUserId, body) {
        return doUpdateCall(`${url.mentorships}/students/${studentUserId}/quiz`, body);
    },
    // Student→tutor question tags (design §10, slice T3; backend T1/T2 PRs
    // #478/#479). Both flags dark ⇒ every call 404s gracefully.
    // status=OPEN|ACKNOWLEDGED|RESOLVED, counterpartUserId — both optional.
    listQuestionTags(params = {}) {
        const q = new URLSearchParams();
        if (params.status) q.append("status", params.status);
        if (params.counterpartUserId) q.append("counterpartUserId", params.counterpartUserId);
        const qs = q.toString();
        return doGetCall(`${url.mentorshipTags}${qs ? `?${qs}` : ""}`);
    },
    // Body: { tutorUserId, questionId, note? } — the tagging student is ALWAYS
    // the JWT principal server-side (§10.3-C1); there is no student field to send.
    createQuestionTag(body) {
        return doUpdateCall(url.mentorshipTags, body);
    },
    // Student retraction (DELETE, soft-delete server-side; returns the DTO).
    retractQuestionTag(tagId) {
        return doDeleteCall(`${url.mentorshipTags}/${tagId}`);
    },
    // Tutor lifecycle (T4; §10.3-M1 row-resolved ownership server-side). PUT
    // with no body — the target tutor is read from the existing row by path id.
    // Every tutor by-id route answers ONE uniform 404 for missing/not-yours/
    // revoked (§10.3-N4) — callers must not distinguish.
    acknowledgeQuestionTag(tagId) {
        return doUpdateCall(`${url.mentorshipTags}/${tagId}/acknowledge`, undefined, true);
    },
    resolveQuestionTag(tagId) {
        return doUpdateCall(`${url.mentorshipTags}/${tagId}/resolve`, undefined, true);
    },
    // Tutor detail view (T4): tag + the student's attempts on the tagged
    // question ({tag, attempts:[{questionStem, answerGiven, correct, ...}]}).
    // VIEW_PROGRESS-gated server-side (§10.3-R3); same uniform 404 seam.
    getQuestionTagContext(tagId) {
        return doGetCall(`${url.mentorshipTags}/${tagId}/context`);
    }
};
